import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  UserCircleIcon, 
  ChevronDownIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  StarIcon,
  CurrencyRupeeIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline'
import BankAccountManagement from '../owner/BankAccountManagement'

const ProfileDropdown = ({ 
  user, 
  profile, 
  restaurant,
  showProfileMenu, 
  setShowProfileMenu,
  setActiveTab,
  handleLogout 
}) => {
  const [showBankManagement, setShowBankManagement] = useState(false)
  const userName = profile?.full_name || user?.email?.split('@')[0] || 'Owner'
  const userEmail = user?.email || 'owner@restaurant.com'

  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowProfileMenu(!showProfileMenu)}
        className={`flex items-center space-x-3 p-1.5 pr-3 rounded-full transition-all duration-300 ${
          showProfileMenu 
            ? 'bg-gray-100 shadow-inner' 
            : 'bg-white hover:bg-gray-50'
        }`}
      >
        {/* Avatar Area */}
        <div className="relative">
          <div className="w-9 h-9 bg-gray-900 rounded-full flex items-center justify-center shadow-lg shadow-gray-200 overflow-hidden ring-2 ring-white">
            {profile?.avatar_url ? (
               <img src={profile.avatar_url} alt={userName} className="w-full h-full object-cover" />
            ) : (
               <span className="text-sm font-black text-white uppercase">{userName.charAt(0)}</span>
            )}
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
        </div>
        
        {/* User Info */}
        <div className="hidden sm:block text-left">
          <p className="text-sm font-black text-gray-900 leading-none mb-0.5 truncate max-w-[120px]">
            {userName}
          </p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">
            Owner
          </p>
        </div>
        
        <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-500 ${showProfileMenu ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {showProfileMenu && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/5 z-40" 
              onClick={() => setShowProfileMenu(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 400 }}
              className="absolute right-0 top-full mt-4 w-80 bg-white rounded-[32px] shadow-2xl shadow-gray-200 border border-gray-100 z-50 overflow-hidden"
            >
              {/* Header */}
              <div className="px-8 py-8 bg-gray-50/50">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-4">
                     <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center shadow-2xl shadow-gray-200 ring-4 ring-white">
                        <span className="text-2xl font-black text-white uppercase">{userName.charAt(0)}</span>
                     </div>
                     <div className="absolute top-0 right-0 p-1 bg-white rounded-full shadow-sm border border-gray-100">
                        <StarIcon className="h-4 w-4 text-amber-500 fill-amber-500" />
                     </div>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 tracking-tight">{userName}</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 italic">{userEmail}</p>
                  
                  <div className="mt-4 px-3 py-1 bg-orange-50 text-orange-600 text-[10px] font-black rounded-full uppercase tracking-widest">
                     Premium Partner
                  </div>
                </div>
              </div>
              
              {/* Menu */}
              <div className="p-4 space-y-1">
                {[
                  { id: 'analytics', name: 'Performance', icon: ChartBarIcon, color: 'blue', desc: 'Deep dive into your data' },
                  { id: 'payments', name: 'Bank & Payouts', icon: CurrencyRupeeIcon, color: 'emerald', desc: 'Settlements & split info' },
                  { id: 'settings', name: 'Preferences', icon: CogIcon, color: 'purple', desc: 'APP & notification settings' },
                ].map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => {
                      setShowProfileMenu(false);
                      if (item.id === 'payments') setShowBankManagement(true);
                      else setActiveTab?.(item.id);
                    }}
                    className="w-full text-left p-4 rounded-2xl transition-all duration-300 hover:bg-gray-50 group flex items-center gap-4"
                  >
                    <div className={`p-2.5 rounded-xl bg-${item.color}-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-${item.color}-100`}>
                      <item.icon className={`h-5 w-5 text-${item.color}-500`} />
                    </div>
                    <div>
                      <p className="text-sm font-black text-gray-900">{item.name}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{item.desc}</p>
                    </div>
                  </button>
                ))}
                
                <div className="h-px bg-gray-50 my-2 mx-4"></div>
                
                <button 
                  onClick={() => {
                    setShowProfileMenu(false);
                    handleLogout?.();
                  }}
                  className="w-full text-left p-4 rounded-2xl transition-all duration-300 hover:bg-rose-50 group flex items-center gap-4"
                >
                  <div className="p-2.5 rounded-xl bg-rose-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-rose-100">
                    <ArrowRightOnRectangleIcon className="h-5 w-5 text-rose-500" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-rose-600">Sign Off</p>
                    <p className="text-[10px] font-bold text-rose-300 uppercase tracking-tighter">Terminate session</p>
                  </div>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
 
      {/* Bank Account Management Modal (Preserved Functionality) */}
      <AnimatePresence>
        {showBankManagement && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" 
              onClick={() => setShowBankManagement(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-4 md:inset-10 lg:inset-20 bg-white rounded-[40px] shadow-2xl z-[70] overflow-hidden border border-gray-100"
            >
              {/* Modal Header */}
              <div className="bg-white px-10 py-8 flex items-center justify-between border-b border-gray-50">
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
                    <BuildingLibraryIcon className="h-8 w-8 text-emerald-500" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-900 tracking-tight">Financial Hub</h2>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">Manage bank accounts & split payout logic</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBankManagement(false)}
                  className="w-12 h-12 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-400" />
                </button>
              </div>
              
              <div className="h-full overflow-y-auto pb-32">
                <BankAccountManagement 
                  restaurant={restaurant} 
                  user={user} 
                  onClose={() => setShowBankManagement(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProfileDropdown